import { TFunction } from 'i18next';
import { useMemo } from 'react';
import { Wildfire } from 'features/wildfires/wildfire.types';
import * as Yup from 'yup';
import { isSupportedEmbedUrl } from 'shared/embedHelpers';
import { AnyObject } from 'yup/lib/types';
import ObjectSchema, { AssertsShape, TypeOfShape } from 'yup/lib/object';
import { getCoordinateValidationSchema } from 'features/reports/components/ReportForm/utils';
import { isValid } from 'date-fns';
import { getAcreageAndContainmentValidationSchema } from 'features/wildfires/utils/getAcreageAndContainmentValidationSchema';
import { getReportMessageValidationSchema } from 'features/wildfires/utils/getReportMessageValidationSchema';
import { RelatedWildfire } from 'features/wildfires/wildfireGeoEventData.types';
import { EvacZone, FormEvacZoneStatus } from 'shared/types';
import { GeoEventUrgencyLevel } from 'features/geoEvents/geoEvent.types';
import { getEvacZoneStatusesValidationSchema } from 'features/geoEvents/utils/getEvacZoneStatusesValidationSchema';
import { getLinksValidationSchema } from 'components/FormLinks';
import { reporterNotesValidationSchema } from 'features/reporterNotes/reporterNotes.utils';
import { getEvacuationFieldsValidationSchema } from 'features/evacZones/utils/getEvacuationFieldsValidationSchema';
import { validateChildGeoEventsForEvacZones } from 'features/geoEvents/geoEvent.utils';
import { validateDeactivationForEvacZones } from 'features/geoEvents/validateDeactivationForEvacZones';
import { GeoEventTypes } from '../../../../../constants';

export const validatePrescribedDateStartLocal = (params: {
  date: Date | null;
  initialDate?: Date;
}): boolean => {
  const { date, initialDate } = params;
  if (!date) return true;
  if (!isValid(date)) return false;
  if (initialDate) {
    if (date.setSeconds(0, 0) < initialDate.setSeconds(0, 0)) {
      // When editing an incident's prescribed start date, ensure that if the new date
      // is earlier than the initial date, it still remains in the future relative
      // to the current date
      return date.setSeconds(0, 0) > new Date().setSeconds(0, 0);
    }
    return true;
  }
  return date.setSeconds(0, 0) > new Date().setSeconds(0, 0);
};

export const useValidationSchema = (
  geoEvent: Wildfire | undefined,
  regionEvacZones: EvacZone[],
  t: TFunction,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): ObjectSchema<any, AnyObject, TypeOfShape<any>, AssertsShape<any>> => {
  const geoEventPrescribedDateStartLocal =
    geoEvent?.data.prescribedDateStartLocal;

  const regionHasEvacZones = regionEvacZones.length > 0;

  return useMemo(
    () =>
      Yup.object().shape(
        {
          name: Yup.string().required(
            t('createEditWildfire.inputs.name.required'),
          ),
          address: Yup.string().when('isComplexParent', {
            is: true,
            then: Yup.string().nullable(),
            otherwise: Yup.string().required(
              t('createEditWildfire.inputs.name.required'),
            ),
          }),
          regions: Yup.array().when('childWildfires', {
            is: (childWildfires: RelatedWildfire[]) =>
              childWildfires && childWildfires.length > 0,
            then: Yup.array().min(0),
            otherwise: Yup.array().min(
              1,
              t('editGeoEvent.inputs.counties.required'),
            ),
          }),
          status: Yup.string()
            .required(t('createEditWildfire.inputs.status.required'))
            .test(
              'no-deactivation-with-evac-zones',
              t(
                'createEditWildfire.inputs.status.deactivationBlockedByEvacZones',
              ),
              function (value) {
                return validateDeactivationForEvacZones({
                  isDeactivating: value === 'inactive',
                  evacZoneStatuses: this.parent.evacZoneStatuses ?? [],
                });
              },
            ),
          notificationType: Yup.string().required(
            t('createEditWildfire.inputs.notificationType.required'),
          ),
          ...getAcreageAndContainmentValidationSchema(t),
          isComplexParent: Yup.boolean(),
          isFps: Yup.boolean(),
          isPrescribed: Yup.boolean(),
          ...getEvacuationFieldsValidationSchema(t, regionHasEvacZones),
          reportNotificationType: Yup.string(),
          reportMessage: Yup.string().when([], {
            is: () => !geoEvent?.id,
            then: getReportMessageValidationSchema(t),
            otherwise: Yup.string(),
          }),
          lat: Yup.number(),
          lng: Yup.number(),
          prescribedDateStartLocal: Yup.mixed()
            .nullable()
            .test(
              'is-future-date',
              t('createEditWildfire.inputs.prescribedDateStart.invalidError'),
              (value) => {
                return validatePrescribedDateStartLocal({
                  date: value,
                  initialDate: geoEventPrescribedDateStartLocal
                    ? new Date(geoEventPrescribedDateStartLocal)
                    : undefined,
                });
              },
            ),
          activeEvacuations: Yup.boolean(),
          ...getEvacZoneStatusesValidationSchema(
            t,
            regionEvacZones,
            geoEvent?.evacZoneStatuses || [],
          ),
          confirmSelectedZones: Yup.boolean().when('confirmSelectedZones', {
            is: (exist: boolean) => typeof exist === 'boolean',
            then: Yup.boolean().oneOf([true]),
            otherwise: Yup.boolean(),
          }),
          confirmAdditionalRegions: Yup.boolean().when(
            'confirmAdditionalRegions',
            {
              is: (exist: boolean) => typeof exist === 'boolean',
              then: Yup.boolean().oneOf([true]),
              otherwise: Yup.boolean(),
            },
          ),
          confirmRemovedRegions: Yup.boolean().when('confirmRemovedRegions', {
            is: (exist: boolean) => typeof exist === 'boolean',
            then: Yup.boolean().oneOf([true]),
            otherwise: Yup.boolean(),
          }),
          asset: Yup.string().nullable(),
          media: Yup.mixed().nullable(),
          provisionalSubmissionMediaId: Yup.number().nullable(),
          mediaUrl: Yup.string().nullable(),
          fileType: Yup.string().nullable(),
          embedUrl: Yup.string()
            .url(t('addIncidentReport.inputs.embedUrl.invalidError'))
            .test(
              'is-supported-embed-url',
              t('addIncidentReport.inputs.embedUrl.unsupportedError'),
              (value) => (value ? isSupportedEmbedUrl(value) : true),
            ),
          mediaLat: getCoordinateValidationSchema({
            t,
            coordType: 'latitude',
            whenPropKey: 'mediaLng',
          }),
          mediaLng: getCoordinateValidationSchema({
            t,
            coordType: 'longitude',
            whenPropKey: 'mediaLat',
          }),
          mediaAz: Yup.number()
            .typeError(t('addIncidentReport.inputs.azimuth.typeError'))
            .min(0, t('addIncidentReport.inputs.azimuth.minError'))
            .max(360, t('addIncidentReport.inputs.azimuth.maxError')),
          expandLinks: Yup.boolean(),
          links: getLinksValidationSchema(t),
          reporterOnlyNotes: Yup.string().nullable(),
          childWildfires: Yup.array().test(
            'is-update-valid',
            '',
            function (childWildfires: RelatedWildfire[] = []) {
              const result = validateChildGeoEventsForEvacZones({
                childGeoEvents: childWildfires,
                evacZoneStatuses:
                  (this.parent.evacZoneStatuses as FormEvacZoneStatus[]) ?? [],
                originalChildGeoEvents: geoEvent?.childGeoEvents?.filter(
                  (child): child is RelatedWildfire =>
                    child.geoEventType === GeoEventTypes.wildfire,
                ),
              });

              if (result.valid) return true;

              return this.createError({
                message: t(
                  'createEditWildfire.inputs.childWildfires.invalidError',
                  {
                    counties: result.missingRegionNames,
                    incidents: result.missingChildIncidents,
                  },
                ),
              });
            },
          ),
          manualDeactivationStarted: Yup.boolean()
            .nullable()
            .test(
              'no-deactivation-with-evac-zones',
              t(
                'createEditWildfire.inputs.status.deactivationBlockedByEvacZones',
              ),
              function (value) {
                return validateDeactivationForEvacZones({
                  isDeactivating: value === true,
                  evacZoneStatuses: this.parent.evacZoneStatuses ?? [],
                });
              },
            ),
          expandInternalLinks: Yup.boolean(),
          internalLinks: getLinksValidationSchema(t),
          urgency: Yup.string().oneOf(Object.values(GeoEventUrgencyLevel)),
          ...reporterNotesValidationSchema,
        },
        [
          ['mediaLat', 'mediaLng'],
          ['confirmSelectedZones', 'confirmSelectedZones'],
          ['confirmAdditionalRegions', 'confirmAdditionalRegions'],
          ['confirmRemovedRegions', 'confirmRemovedRegions'],
        ],
      ),
    [
      geoEvent?.childGeoEvents,
      geoEvent?.evacZoneStatuses,
      geoEvent?.id,
      geoEventPrescribedDateStartLocal,
      regionEvacZones,
      regionHasEvacZones,
      t,
    ],
  );
};
